import { useMemo } from 'react';
import classNames from 'classnames';
import {
    useTimelineCondensePellets,
    useTimelineInterval,
    useTimelinePixelWidth,
} from 'app/agriculture/AgTimeline/context/TimelineContext';
import { Interval } from 'app/agriculture/AgTimeline/types';
import {
    PELLET_ROW_HEIGHT,
    PELLET_ROW_HEIGHT_CONDENSED,
    COLLAPSE_INTERVAL_TO_POINT,
    COLLAPSE_INTERVAL_TO_POINT_CONDENSED,
    MOVE_INTERVAL_TEXT_OUTSIDE,
} from './consts';
import { useStackingContext } from 'app/agriculture/AgTimeline/components/TimelinePellet/StackingContext/StackingContext';
import { getIntervalPositionWithinInterval } from 'app/agriculture/AgTimeline/utils/getIntervalPositionWithinInterval';

export interface TimelinePelletContainerProps {
    id: string;
    interval: Interval<Date>;
    rowOffset?: number;
}

export function TimelinePelletContainer({
    id,
    interval: pelletInterval,
    children,
}: TimelinePelletContainerProps & {
    children?: React.ReactNode;
}) {
    const stackingContext = useStackingContext();
    const condensed = useTimelineCondensePellets();
    const timelineInterval = useTimelineInterval();
    const timelinePixelWidth = useTimelinePixelWidth();

    const rowOffset = stackingContext.rows.findIndex((row) =>
        row.some((pellet) => pellet.id === id),
    );

    const { left, right, width } = useMemo(() => {
        const { left, right } = getIntervalPositionWithinInterval(pelletInterval, timelineInterval);
        return { left, right, width: 1 - left - right };
    }, [pelletInterval, timelineInterval]);

    const pelletPixelWidth = width * timelinePixelWidth;
    const isPoint =
        pelletPixelWidth <
        (condensed ? COLLAPSE_INTERVAL_TO_POINT_CONDENSED : COLLAPSE_INTERVAL_TO_POINT);
    const shouldTextBeOutside = pelletPixelWidth < MOVE_INTERVAL_TEXT_OUTSIDE;

    return (
        <div
            id={id}
            data-testid="TimelinePellet"
            className={classNames('TimelinePellet', {
                'TimelinePellet--interval': !isPoint,
                'TimelinePellet--text-outside-interval': shouldTextBeOutside,
                'TimelinePellet--point': isPoint,
                'TimelinePellet--condensed': condensed,
                'TimelinePellet--startOut': left < 0,
                'TimelinePellet--endOut': right < 0,
            })}
            style={{
                left: `${Math.max(left, 0) * 100}%`,
                right: `${Math.max(right, 0) * 100}%`,
                top: rowOffset * (condensed ? PELLET_ROW_HEIGHT_CONDENSED : PELLET_ROW_HEIGHT),
            }}
        >
            {children}
        </div>
    );
}
